// @lat: [[tasks#SEO Tasks#Frontend#Map plan]]
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Network, Plus, RefreshCw, Loader2, ArrowRight, Sparkles } from "lucide-react";
import * as api from "../../lib/api";
import { useOrgId } from "../../hooks/useOrgId";
import { MOVES, TIERS, buildPlan, readMapSelection } from "../../lib/queryMapPlan";
import { PRIMARY, PriorityLabel } from "./shared";

const BULK = 5;

function MoveChip({ move }) {
  const m = MOVES[move];
  return (
    <span
      className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold whitespace-nowrap"
      style={{ color: m.color, backgroundColor: `${m.color}14` }}
    >
      {m.label}
    </span>
  );
}

function TierChip({ tier }) {
  const t = TIERS[tier] || TIERS.unscored;
  return (
    <span
      className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold whitespace-nowrap border border-zinc-200"
      style={{ backgroundColor: t.bg, color: t.text }}
    >
      {t.label}
    </span>
  );
}

/** Ranked task recommendations from what's on the Query Map, each one click from a task. */
export default function MapPlanTab({ tasks, onCount, onCreated }) {
  const orgId = useOrgId();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(new Set());

  const load = useCallback(async () => {
    setError("");
    try {
      const [t, q] = await Promise.all([
        api.getQueryTopics({ limit: 200 }),
        api.getPerformanceQueries({ limit: 500, sort: "commercial_value", order: "desc" }).catch(() => ({ queries: [] })),
      ]);
      setData({ topics: t.topics || [], queries: q.queries || [] });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load search topics.");
      setData({ topics: [], queries: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Re-read the map's selection on each load: it lives in this browser, saved by the Query Map.
  const plan = useMemo(
    () => (data ? buildPlan({ ...data, selection: readMapSelection(orgId) }) : null),
    [data, orgId],
  );

  // A recommendation already turned into an open (or finished) task shows its key instead.
  const taskByTitle = useMemo(() => {
    const m = new Map();
    (tasks || []).filter(t => t.status !== "canceled").forEach(t => m.set(t.title.trim().toLowerCase(), t));
    return m;
  }, [tasks]);
  const existing = (item) => taskByTitle.get(item.task.title.trim().toLowerCase());
  const open = plan ? plan.items.filter(i => !existing(i)) : [];

  useEffect(() => { onCount?.(plan ? open.length : null); }, [plan, open.length, onCount]);

  const create = async (items) => {
    setBusy(b => new Set([...b, ...items.map(i => i.id)]));
    const made = [];
    try {
      for (const item of items) made.push(await api.createTask(item.task));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create task.");
    } finally {
      setBusy(b => new Set([...b].filter(id => !items.some(i => i.id === id))));
      if (made.length) onCreated?.(made);
    }
  };

  if (!plan) {
    return <div className="py-10 text-center text-xs text-zinc-400">Loading…</div>;
  }

  const counts = open.reduce((acc, i) => {
    const a = MOVES[i.move].label;
    acc[a] = (acc[a] || 0) + 1;
    return acc;
  }, {});
  const bridges = plan.items.filter(i => i.kind === "bridge").length;

  return (
    <div className="space-y-3">
      {error && (
        <div className="px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">{error}</div>
      )}

      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Network size={14} className="text-zinc-900" />
              <p className="text-sm font-bold text-zinc-900">
                {open.length ? `${open.length} recommended task${open.length === 1 ? "" : "s"}` : "Every map recommendation has a task"}
              </p>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              From {plan.items.length - bridges} topic card{plan.items.length - bridges === 1 ? "" : "s"}
              {bridges ? ` and ${bridges} missing link${bridges === 1 ? "" : "s"}` : ""} on your Query Map
              {plan.fromDefault ? " (the default top topics: open the map to choose your own)" : ""}
              {plan.focus ? ` · focus: ${plan.focus}` : ""}.
              {" "}Ranked by lead potential, then next move, then buyer fit, never by search volume.
            </p>
            {Object.keys(counts).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Object.entries(counts).map(([label, n]) => (
                  <span key={label} className="px-2 py-0.5 rounded-full bg-zinc-100 text-[11px] text-zinc-600">
                    {label} <span className="font-bold tabular-nums">{n}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} title="Refresh" className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50">
              <RefreshCw size={13} />
            </button>
            <Link
              to="/dashboard/query-map"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
            >
              Open map <ArrowRight size={12} />
            </Link>
            {open.length > 0 && (
              <button
                onClick={() => create(open.slice(0, BULK))}
                disabled={busy.size > 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white rounded-lg disabled:opacity-40"
                style={{ backgroundColor: PRIMARY }}
              >
                <Plus size={12} /> Create top {Math.min(BULK, open.length)}
              </button>
            )}
          </div>
        </div>
      </div>

      {plan.items.length === 0 && (
        <div className="py-10 text-center text-xs text-zinc-400">
          Nothing to act on yet. Add topics to the Query Map, or run Search Console analysis to build search topics.
        </div>
      )}

      {plan.items.map((item, i) => {
        const task = existing(item);
        return (
          <div key={item.id} className="flex items-start gap-4 bg-white rounded-xl border border-zinc-100 shadow-sm px-5 py-3.5">
            <span className="w-5 pt-0.5 text-right text-xs font-bold text-zinc-300 tabular-nums">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <MoveChip move={item.move} />
                {item.kind === "bridge" ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-bold text-violet-600 bg-violet-50 whitespace-nowrap">
                    <Sparkles size={10} /> Missing link
                  </span>
                ) : <TierChip tier={item.tier} />}
                <PriorityLabel priority={item.task.priority} />
              </div>
              <p className={`text-sm font-bold ${task ? "text-zinc-400" : "text-zinc-900"}`}>{item.label}</p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {item.kind === "bridge"
                  ? `Between "${item.between[0]}" and "${item.between[1]}"${item.why ? ` — ${item.why}` : ""}`
                  : [
                      item.buyerFit != null ? `fit ${Number(item.buyerFit).toFixed(2)}` : null,
                      item.why || null,
                      `${item.task.queries.length} searches · ${Number(item.impressions).toLocaleString()} impr`,
                    ].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex-shrink-0">
              {task ? (
                <Link
                  to={`/dashboard/tasks/${task.key}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50"
                >
                  {task.key} <ArrowRight size={12} />
                </Link>
              ) : (
                <button
                  onClick={() => create([item])}
                  disabled={busy.has(item.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white rounded-lg disabled:opacity-40"
                  style={{ backgroundColor: PRIMARY }}
                >
                  {busy.has(item.id) ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create task
                </button>
              )}
            </div>
          </div>
        );
      })}

      {plan.skipped.length > 0 && (
        <p className="text-[11px] text-zinc-400 px-1">
          No task needed for {plan.skipped.map(s => `${s.label} (${MOVES[s.move]?.label.toLowerCase() || s.move})`).join(", ")}.
        </p>
      )}
    </div>
  );
}
